
import React from "react";
import styles from "../assets/style.module.css";
import * as ReactRouterDom from "react-router-dom";
import { ApiResponseData, BaseService, IGallery, IProfileWebsite, ITournamentEditionWebsite, OmneHostService, SanitizedResponse } from "../../../../services";
import { alertDialog, Classname, FileHelper, fileInputBuildFileInputPreview, FileInputPreviewType, Paginator, Scheme, useSearchParams } from "@rivtn/noseur";
import { Utils } from "../../../../utils";
import { i18nManager } from "../../../../i18n";

export function buildGalleryView(g: IGallery) {
    const previewType = FileHelper.detectPreviewTypeFromUrl(g.media_location + g.extra_data?.extension);

    return (<div key={g.external_id} className={styles["gal-item"]}>
        {fileInputBuildFileInputPreview({
            previewType,
            url: g.media_location,
            style: {
                width: "100%",
                height: "100%",
            }
        })}
        {(previewType === FileInputPreviewType.IMAGE) && (<div className={styles["gal-overlay"]} onClick={() => {
            alertDialog({
                cancel: null,
                confirm: {
                    label: i18nManager.Labels.gallery.close,
                    props: {
                        textOnly: true,
                        style: {
                            color: "var(--text)"
                        }
                    }
                },
                componentProps: {
                    maximized: true,
                    contentProps: {
                        style: {
                            background: "var(--dark)",
                        },
                    }
                },
                message: fileInputBuildFileInputPreview({
                    previewType,
                    url: g.media_location,
                    style: {
                        margin: "auto",
                    }
                })
            }).show();
        }}><i className="fa fa-magnifying-glass-plus" /></div>)}
        {(g.action_location || g.action_label) && (<ReactRouterDom.NavLink target='_blank' to={g.action_location} className={({ }) => Classname.build(styles["gal-tag"])}>{g.action_label ?? g.extra_data?.tags?.join(", ")}</ReactRouterDom.NavLink>)}
    </div>);
}

export interface GalleryProps {
    website: ITournamentEditionWebsite;
}

function Gallery(props: GalleryProps) {

    const labels = i18nManager.Labels.gallery;
    const omnehostService = OmneHostService.getInstance();
    const [tags, setTags] = React.useState<Set<string>>(new Set());
    const [galleryFilter, setGalleryFilter] = React.useState<string>();
    const [gallery, setGallery] = React.useState<ApiResponseData<IGallery>>();
    const { page, setPage } = useSearchParams({ defaultInit: window.location.search, params: { page: 1 } });

    React.useEffect(fetchInformation, [omnehostService, page]);

    return (<div className={styles["section"]}>
        <div className={styles["sec-line"]} />
        <h1 className={styles["sec-title"]}>{labels.photo} <em>{labels.gallery}</em></h1>
        <p className={styles["sec-sub"]}>{labels.desc}</p>
        <div className={styles["tabs"]} style={{ marginBottom: "2rem" }}>
            {Array.from(tags).map((t) => (
                <button key={t} className={Classname.build(styles[`tab-btn`], { [styles["active"]]: galleryFilter === t })} onClick={() => setGalleryFilter(t)}>{t}</button>
            ))}
        </div>

        {(!!gallery?.content)
            ? ((gallery.content && gallery.content?.length > 0)
                ? (<div className={styles["gal-grid"]}>
                    {gallery.content!.filter((g) => {
                        return (!galleryFilter || g.extra_data?.tags?.includes(galleryFilter));
                    }).map(buildGalleryView)}
                </div>)
                : (Utils.emptyState()))
            : (Utils.loadingState())}

        <Paginator scheme={Scheme.LIGHT} style={{ marginTop: 50 }} rowsPerPage={gallery?.size} totalRecords={gallery?.total_elements} initialPage={parseInt("" + page)} onPageChange={(e) => {
            setPage(e.currentPage)
        }} template={{
            activePageLabel: () => "",
            nextPageElement: (option) => (<button className={Classname.build(`${styles["pg-btn"]}`, option.className, "fa fa-angle-right")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
            previousPageElement: (option) => (<button className={Classname.build(`${styles["pg-btn"]}`, option.className, "fa fa-angle-left")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
            lastPageElement: (option) => (<button className={Classname.build(`${styles["pg-btn"]}`, option.className, "fa fa-angle-double-right")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
            firstPageElement: (option) => (<button className={Classname.build(`${styles["pg-btn"]}`, option.className, "fa fa-angle-double-left")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
            pageElement: (option) => (<button className={Classname.build(`${styles["pg-btn"]}`, option.className, (option.active ? styles["active"] : null))} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick}>{option.label}</button>),
        }} />
    </div>);

    function fetchInformation() {
        setGallery({});
        omnehostService.queryTournamentEditionGallery(props.website.tournament_edition.external_id, {
            page,
            sort: "updated_at,DESC",
        }).then(({ apiResponse, content }: SanitizedResponse<IGallery>) => {
            setGallery(apiResponse.data!);
            setTags(new Set(content?.reduce((acc, v) => acc.concat(v.extra_data?.tags ?? []), [] as string[]) ?? []));
        }).catch(BaseService.reportError);
    }

}

export default Gallery;